import { PlayArrow, RotateRight } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, Slider, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ProductDrawerProps } from '../../../@types/ContextProviderRelated';
import { EUnitType } from '../../../@types/Enums';
import { IObject } from '../../../models/standard/ObjectModel';
import ProductModel from '../../../models/standard/ProductModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { deleteButton, immersightBlue, immersightBlue75, immersightGreyLighter3 } from '../../../utils/colors';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeListFixed from '../AttributeListFixed';

const ProductDrawer: React.FC<ProductDrawerProps> = ({ id, open, onClose, selectedProduct, resetSelection }) => {
  const intl = useIntl();
  const { locale } = intl; 
  // FIX: Destructure allProducts from building context
  const { allProducts, setProductRotation, deleteProduct, updateProduct } = useBuilding();
  
  // FIX: Access product from flattened allProducts list using selection index
  const product = allProducts[selectedProduct.selectedProductIndex];
  
  const initialRotation = product ? Math.round(product.rotation * (180 / Math.PI)) : 0;
  
  const [rotation, setRotation] = useState<number>(initialRotation);
  const [rotationInput, setRotationInput] = useState<number>(initialRotation);
  const [shifting, setShifting] = useState<number>(product?.texture?.offset ?? 0);
  const [textureRotation, setTextureRotation] = useState<number>(product?.texture?.rotation ? Math.round(product.texture.rotation * (180 / Math.PI)) : 0);

  const getLocalizedObjectName = useCallback((obj: IObject): string => {
    const currentLocale = locale.split('-')[0];
    if (obj.localization) {
      return obj.localization[currentLocale] || obj.localization.en || obj.name;
    }
    return obj.name;
  }, [locale]);

  useEffect(() => {
    if (product) {
      const newRotation = Math.round(product.rotation * (180 / Math.PI));
      if (newRotation !== rotation) {
        setRotation(newRotation);
        setRotationInput(newRotation);
      }
      setShifting(product?.texture?.offset ?? 0);
      setTextureRotation(product?.texture?.rotation ? Math.round(product.texture.rotation * (180 / Math.PI)) : 0);
    } else {
      onClose();
    }
  }, [product?.rotation, product?.texture, onClose, rotation]);

  const validateAndSetRotation = useCallback(
    (value: number): void => {
      if (product) {
        const normalizedValue = Math.round(((value % 360) + 360) % 360);
        setRotation(normalizedValue);
        setRotationInput(normalizedValue);
        // FIX: Use product.uuid instead of ID, passed as string
        setProductRotation(product.uuid, (normalizedValue * Math.PI) / 180);
      }
    },
    [product, setProductRotation],
  );

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [resetSelection, onClose]);

  const handleDeleteProduct = useCallback(() => {
    if (product) {
      deleteProduct(product.uuid);
      resetSelection();
      onClose();
    }
  }, [product, deleteProduct, resetSelection, onClose]);

  const updateTexture = (property: keyof NonNullable<ProductModel['texture']>, value: number) => {
    if (!product || !product.texture) return;
    const updatedTexture = {
      ...product.texture,
      [property]: property === 'rotation' ? (value * Math.PI) / 180 : value,
    };
    const updatedProduct = new ProductModel(
      product.id,
      product.name,
      product.svgPath,
      product.dimensions.length,
      product.dimensions.height || 0,
      product.dimensions.depth,
      product.position,
      product.rotation,
      product.fillColor,
      product.strokeColor,
      product.strokeWidth,
      updatedTexture,
      product.attributes,
      product.objects,
      product.description,
      product.manufacturer,
      product.uuid,
      product.country
    );
    // FIX: Pass updatedProduct directly, remove index param
    updateProduct(updatedProduct);
  };

  if (!product) {
    return null;
  }

  const rotationWheelConfig: WheelInputConfig = {
    unitType: EUnitType.METRIC,
    minMetric: 0,
    maxMetric: 360,
    minInches: 0,
    maxInches: 360,
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="ProductDrawer.header" />
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
        <Grid container item direction="column" spacing={3} p={3}>
          <Grid item>
            <Typography variant="h6" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              {product.name}
            </Typography>
            {product.manufacturer && (
              <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a' }}>
                {typeof product.manufacturer === 'string' ? product.manufacturer : product.manufacturer.name}
              </Typography>
            )}
            {product.description && (
              <Typography variant="body2" gutterBottom style={{ color: '#4a4a4a' }}>
                {product.description}
              </Typography>
            )}
          </Grid>

          {/* OBJECT CATEGORIES (LOCALIZED) */}
          {product.objects && product.objects.length > 0 && (
            <Grid item>
              <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
                <FormattedMessage id="RoomComponentsSearchPanel.categories" defaultMessage="Categories" />:{' '}
                {product.objects.map((obj) => getLocalizedObjectName(obj)).join(', ')}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              <FormattedMessage id="ProductDrawer.rotation" />
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={rotationInput}
              onChange={(event) => setRotationInput(parseFloat(event.target.value))}
              onBlur={() => validateAndSetRotation(rotationInput)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  validateAndSetRotation(rotationInput);
                  event.currentTarget.blur();
                }
              }}
              onWheel={(e) =>
                handleWheelInput(
                  e,
                  rotationInput.toString(),
                  (v: string) => {
                    const newVal = parseFloat(v);
                    if (!isNaN(newVal)) {
                      setRotationInput(newVal);
                    }
                  },
                  rotationWheelConfig,
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RotateRight />
                  </InputAdornment>
                ),
                inputProps: { min: 0, max: 360, step: 1 },
              }}
            />
          </Grid>

          {/* TEXTURE CONTROLS */}
          {product.texture && (product.texture.material || product.texture.color) && (
            <>
              <Grid item>
                <Typography variant="subtitle2" sx={{ color: immersightBlue }}>
                  <FormattedMessage id="ProductDrawer.surface" defaultMessage="SURFACE" />
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, marginTop: '0' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: product.texture.color || 'transparent',
                    backgroundImage: product.texture.material ? `url(${product.texture.material.image.src})` : 'none',
                    backgroundSize: 'cover',
                    border: product.texture.color ? '1px solid #ccc' : 'none',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 120,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {product.texture.name.length > 20 ? `${product.texture.name.slice(0, 20)}...` : product.texture.name}
                </Typography>
              </Grid>
              
              {product.texture.material && (
                <>
                  <Grid item>
                    <Typography variant="subtitle2">
                      <FormattedMessage id="ProductDrawer.textureShifting" defaultMessage="Shift" />
                    </Typography>
                    <Slider
                      value={shifting}
                      min={-2}
                      max={2}
                      step={0.1}
                      onChange={(_, value) => {
                        const newValue = value as number;
                        setShifting(newValue);
                        updateTexture('offset', newValue);
                      }}
                      valueLabelDisplay="auto"
                      sx={{
                        color: immersightBlue,
                        '& .MuiSlider-thumb': { backgroundColor: immersightGreyLighter3 },
                        '& .MuiSlider-rail': { backgroundColor: immersightBlue75 },
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2">
                      <FormattedMessage id="ProductDrawer.textureRotation" defaultMessage="Rotate" />
                    </Typography>
                    <Slider
                      value={textureRotation}
                      min={0}
                      max={360}
                      step={1}
                      onChange={(_, value) => {
                        const newValue = value as number;
                        setTextureRotation(newValue);
                        updateTexture('rotation', newValue);
                      }}
                      valueLabelDisplay="auto"
                      sx={{
                        color: immersightBlue,
                        '& .MuiSlider-thumb': { backgroundColor: immersightGreyLighter3 },
                        '& .MuiSlider-rail': { backgroundColor: immersightBlue75 },
                      }}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {/* ATTRIBUTES (FIXED LIST) */}
          {product.attributes && product.attributes.length > 0 && (
            <Grid item>
              <AttributeListFixed
                attributes={product.attributes}
                originRegion={product.country} 
              />
            </Grid>
          )}

          <Grid item>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteProduct}
              sx={{
                backgroundColor: deleteButton,
                marginTop: '20px',
              }}
            >
              <FormattedMessage id="ProductDrawer.deleteProduct" />
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default ProductDrawer;